<template>
  <div
    class="card-list-item"
    :class="{ 'in-context-pot': isInContextPot, 'selected': isSelected }"
    @click="handleClick"
  >
    <!-- Batch Selection Checkbox -->
    <div v-if="batchSelectionMode" class="selection-checkbox" @click.stop="toggleSelection">
      <el-checkbox :value="isSelected" @change="toggleSelection"></el-checkbox>
    </div>

    <div class="card-list-content">
      <!-- Collection Tags -->
      <div v-if="cardCollections.length > 0" class="collection-tags">
        <el-tag
          v-for="collection in cardCollections"
          :key="`collection-${collection.id}`"
          size="mini"
          :color="getCollectionTagColor(collection.name)"
          class="collection-tag"
        >
          {{ collection.name }}
        </el-tag>
      </div>

      <!-- Title and Content Preview -->
      <div class="title-content-wrapper">
        <span class="card-title">{{ card.title || 'Untitled' }}</span>
        <span class="card-content-preview">{{ contentPreview }}</span>
      </div>

      <!-- Labels -->
      <div v-if="card.labels && card.labels.length > 0" class="labels-section">
        <el-tag
          v-for="(label, index) in card.labels"
          :key="`label-${index}`"
          size="mini"
          type="info"
          class="label-tag"
        >
          {{ label }}
        </el-tag>
      </div>
    </div>

    <!-- Date and Actions -->
    <div class="card-list-meta">
      <span class="card-date">{{ formatDate(card.created_at) }}</span>

      <!-- Reminder Indicator -->
      <div
        v-if="card.reminder_enabled || card.enable_reminder"
        class="reminder-indicator"
        @click.stop
      >
        <i class="el-icon-bell"></i>
      </div>

      <!-- Star Button -->
      <div
        class="star-button"
        :class="{ 'starred': card.is_starred }"
        @click.stop="toggleStar"
        :title="card.is_starred ? 'Unstar' : 'Star'"
      >
        <i :class="card.is_starred ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
      </div>

      <!-- Actions Menu -->
      <el-dropdown
        trigger="click"
        @command="handleCommand"
        @click.stop
        class="actions-dropdown"
      >
        <span class="el-dropdown-link">
          <i class="el-icon-more"></i>
        </span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="addToContext">
            {{ isInContextPot ? 'Remove from Context' : 'Add to Context' }}
          </el-dropdown-item>
          <el-dropdown-item command="edit">Edit</el-dropdown-item>
          <el-dropdown-item v-if="canShare" command="share">Share</el-dropdown-item>
          <el-dropdown-item command="delete" divided>Delete</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardListItem',
  props: {
    card: {
      type: Object,
      required: true
    },
    collections: {
      type: Array,
      default: () => []
    },
    isInContextPot: {
      type: Boolean,
      default: false
    },
    canShare: {
      type: Boolean,
      default: false
    },
    batchSelectionMode: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardCollections() {
      if (!this.collections || this.collections.length === 0) {
        return []
      }

      let collectionIds = []
      if (this.card.collection_ids && Array.isArray(this.card.collection_ids)) {
        collectionIds = this.card.collection_ids
      } else if (this.card.collection_id) {
        collectionIds = [this.card.collection_id]
      }

      if (collectionIds.length === 0) {
        return []
      }

      return this.collections.filter(col => collectionIds.includes(col.id))
    },
    contentPreview() {
      if (!this.card.content) return ''

      // Strip HTML tags and get plain text
      const plainText = this.card.content.replace(/<[^>]*>/g, '')

      // Return first 100 characters
      return plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText
    }
  },
  methods: {
    getCollectionTagColor(collectionName) {
      const colors = [
        '#409eff', '#67c23a', '#e6a23c', '#f56c6c',
        '#9c27b0', '#00bcd4', '#ff9800', '#4caf50'
      ]
      const hash = collectionName.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc)
      }, 0)
      return colors[Math.abs(hash) % colors.length]
    },
    formatDate(timestamp) {
      const date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      if (days > 7) {
        return date.toLocaleDateString()
      } else if (days > 0) {
        return `${days}d ago`
      } else if (hours > 0) {
        return `${hours}h ago`
      } else if (minutes > 0) {
        return `${minutes}m ago`
      } else {
        return 'Just now'
      }
    },
    handleClick(event) {
      console.log('[CardListItem] handleClick triggered', event.target)
      // Check if click originated from action menu, star button, or reminder
      const target = event.target
      if (target && target.closest) {
        const isActionsDropdown = target.closest('.actions-dropdown')
        const isStarButton = target.closest('.star-button')
        const isReminderIndicator = target.closest('.reminder-indicator')
        const isDropdownLink = target.closest('.el-dropdown-link')
        const isMoreIcon = target.classList.contains('el-icon-more')

        console.log('[CardListItem] Click checks:', {
          isActionsDropdown, isStarButton, isReminderIndicator, isDropdownLink, isMoreIcon
        })

        if (isActionsDropdown || isStarButton || isReminderIndicator || isDropdownLink || isMoreIcon) {
          console.log('[CardListItem] Click on action menu detected, ignoring')
          event.stopPropagation()
          event.preventDefault()
          return
        }
      }
      console.log('[CardListItem] Emitting click event with card:', this.card)
      this.$emit('click', this.card)
    },
    toggleSelection() {
      this.$emit('toggle-selection', this.card.id)
    },
    toggleStar() {
      this.$emit('toggle-star', this.card)
    },
    handleCommand(command) {
      switch (command) {
        case 'edit':
          this.$emit('edit', this.card)
          break
        case 'delete':
          this.$emit('delete', this.card)
          break
        case 'addToContext':
          this.$emit('add-to-context', this.card)
          break
        case 'share':
          this.$emit('share', this.card)
          break
      }
    }
  }
}
</script>

<style scoped>
.card-list-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  background-color: white;
  position: relative;
}

.card-list-item:hover {
  background-color: #f5f7fa;
}

.card-list-item.in-context-pot {
  background-color: #ecf5ff;
  border-left: 3px solid #409eff;
}

.card-list-item.selected {
  background-color: #e6f7ff;
  border-left: 3px solid #409eff;
}

.selection-checkbox {
  margin-right: 12px;
  flex-shrink: 0;
}

.card-list-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
}

.collection-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.collection-tag {
  color: white !important;
  border: none !important;
  font-size: 11px;
  height: 18px;
  line-height: 16px;
  padding: 0 6px;
  font-weight: 500;
}

.title-content-wrapper {
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.card-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  flex-shrink: 0;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-content-preview {
  color: #909399;
  font-size: 13px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.labels-section {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.label-tag {
  font-size: 11px;
  height: 18px;
  line-height: 16px;
}

.card-list-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  margin-left: 16px;
}

.card-date {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}

.reminder-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF, #66B1FF);
  color: white;
}

.reminder-indicator i {
  font-size: 10px;
}

.star-button {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transition: all 0.2s;
  color: #909399;
}

.star-button:hover {
  background: #f5f7fa;
  color: #F7BA2A;
}

.star-button.starred {
  color: #F7BA2A;
}

.star-button i {
  font-size: 18px;
}

.actions-dropdown {
  cursor: pointer;
}

.el-dropdown-link {
  color: #909399;
  font-size: 16px;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.el-dropdown-link:hover {
  color: #409eff;
}

/* Responsive */
@media (max-width: 768px) {
  .card-list-item {
    padding: 10px 12px;
  }

  .card-title {
    max-width: 150px;
  }

  .labels-section {
    display: none;
  }
}
</style>
